import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { DocumentDto } from '@lib/dto/document.dto'
import {
  MAX_PHOTO_BYTES,
  isAllowedPhotoContentType,
} from '@lib/constants/documents'
import type { ListDocumentsQuery } from '@lib/input/document.input'
import { videoOwnerId, type VideoOwner } from '@lib/input/tree-video.input'
import { documentService } from '@/services/DocumentService'
import { documentKeys } from '@/hooks/queryKeys'

/**
 * What a set of photographs belongs to: a tree, one problem on it, or one
 * irrigation record.
 *
 * A union of single ids rather than a bag of optional ones, so a set cannot
 * name two subjects or none. "A photo of the tree" and "a photo of one problem
 * on it" are genuinely different subjects — one shows what the tree looks
 * like, the other is the evidence for a finding — and the API treats them as
 * separate scopes for exactly that reason. A valve and its problems are the
 * same pair.
 *
 * A tree, and each irrigation record, are the same subjects a video can
 * belong to ({@link VideoOwner}), so a new one of those does not have to be
 * added here by hand. A problem on a tree is a photograph's subject and not
 * a video's. A tree's photos also name the visit the survey is working in.
 * No visit (null, or left out) lists every visit's and uploads to none.
 */
export type PhotoScope =
  | (Extract<VideoOwner, { treeId: string }> & { visitId?: string | null })
  | { treeProblemId: string }
  | Exclude<VideoOwner, { treeId: string }>

/** The list query for a scope, and the cache key that follows from it. */
function scopeQuery(scope: PhotoScope): Partial<ListDocumentsQuery> {
  if ('treeId' in scope) {
    const { visitId, ...tree } = scope
    return {
      ...tree,
      ...(typeof visitId === 'string' ? { visitId } : {}),
      kind: 'PHOTO',
    }
  }
  return { ...scope, kind: 'PHOTO' }
}

/**
 * The ids a scope names.
 *
 * Not `Object.values`: on a union that returns `any[]`, so a subject that
 * stopped being a string would still compare with `''`.
 */
function scopeIds(scope: PhotoScope): readonly (string | null | undefined)[] {
  if ('treeId' in scope) return [scope.treeId, scope.visitId]
  if ('treeProblemId' in scope) return [scope.treeProblemId]
  return [videoOwnerId(scope)]
}

/** Empty ids are a sheet that has not settled yet, not something to fetch. */
function scopeIsReady(scope: PhotoScope): boolean {
  return scopeIds(scope).every((id) => id !== '')
}

/**
 * Photographs of one subject (see {@link PhotoScope}).
 *
 * READY only. A document row is committed BEFORE its bytes are uploaded, so
 * that an interrupted upload leaves a visible record rather than an orphaned
 * object — which is right for the document library, where somebody can see the
 * failure and clean it up. On a survey sheet it would mean a permanently
 * broken tile with no explanation beside the error message that already said
 * what happened.
 */
export function usePhotos(
  scope: PhotoScope,
  options: { enabled?: boolean } = {},
): UseQueryResult<DocumentDto[]> {
  const query = scopeQuery(scope)
  return useQuery({
    queryKey: documentKeys.list(query),
    queryFn: async () =>
      (await documentService.list(query)).documents.filter(
        (document) => document.status === 'READY',
      ),
    enabled: (options.enabled ?? true) && scopeIsReady(scope),
    staleTime: 15_000,
  })
}

/**
 * Refuse a file the photo store will not keep.
 *
 * Thrown before the handshake so a 40 MB burst or a video is refused while
 * the person is still looking at the sheet, rather than after a minute of
 * upload on a rural connection. The API enforces the same rules regardless —
 * a browser check is a courtesy, not a control.
 */
export function assertUsablePhoto(file: File): void {
  if (file.size === 0) {
    throw new Error('That photo is empty.')
  }
  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(
      `Photos must be ${String(Math.floor(MAX_PHOTO_BYTES / (1024 * 1024)))} MB or smaller.`,
    )
  }
  if (!isAllowedPhotoContentType(file.type)) {
    throw new Error(
      file.type === ''
        ? 'That file type could not be identified.'
        : 'That is not a photo we can store.',
    )
  }
}

/**
 * The subject a mutation was started for.
 *
 * TanStack Query replaces a pending mutation's callbacks when this hook
 * re-renders, so a closure over `scope` would refresh whichever subject is
 * on screen when the upload finishes. The value is copied into the
 * variables at `mutate`, and that copy does not move.
 */
function useScopeAtCall(scope: PhotoScope): React.RefObject<PhotoScope> {
  const scopeRef = React.useRef(scope)
  React.useLayoutEffect(() => {
    scopeRef.current = scope
  }, [scope])
  return scopeRef
}

/** Upload a photograph of one subject, after {@link assertUsablePhoto}. */
export function useUploadPhoto(
  scope: PhotoScope,
  onProgress?: (percent: number | null) => void,
) {
  const queryClient = useQueryClient()
  const scopeRef = useScopeAtCall(scope)
  const mutation = useMutation<
    DocumentDto,
    Error,
    { file: File; scope: PhotoScope }
  >({
    mutationFn: async ({ file, scope: saved }) => {
      assertUsablePhoto(file)
      return documentService.uploadFile(file, {
        kind: 'PHOTO',
        ...saved,
        ...(onProgress
          ? {
              onProgress: (progress) => {
                onProgress(progress.percent)
              },
            }
          : {}),
      })
    },
    onSuccess: (_document, variables) => {
      void queryClient.invalidateQueries({
        queryKey: documentKeys.list(scopeQuery(variables.scope)),
      })
    },
  })
  return {
    ...mutation,
    mutate: (file: File) => {
      mutation.mutate({ file, scope: scopeRef.current })
    },
    mutateAsync: (file: File) =>
      mutation.mutateAsync({ file, scope: scopeRef.current }),
  }
}

export function useArchivePhoto(scope: PhotoScope) {
  const queryClient = useQueryClient()
  const scopeRef = useScopeAtCall(scope)
  const mutation = useMutation<
    DocumentDto,
    Error,
    { id: string; scope: PhotoScope }
  >({
    mutationFn: ({ id }) => documentService.setArchived(id, true),
    onSuccess: (_document, variables) => {
      void queryClient.invalidateQueries({
        queryKey: documentKeys.list(scopeQuery(variables.scope)),
      })
    },
  })
  return {
    ...mutation,
    mutate: (id: string) => {
      mutation.mutate({ id, scope: scopeRef.current })
    },
    mutateAsync: (id: string) =>
      mutation.mutateAsync({ id, scope: scopeRef.current }),
  }
}

/**
 * A viewable URL for a stored photo.
 *
 * Storage is private — every read is a pre-signed link with a short expiry —
 * so a thumbnail cannot simply point at a bucket path. The link is fetched per
 * photo and cached for rather less than its own lifetime, so a sheet left open
 * on a truck dashboard does not end up showing broken images.
 */
export function usePhotoUrl(
  documentId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<string> {
  return useQuery({
    queryKey: documentKeys.download(documentId),
    queryFn: async () => (await documentService.getDownloadUrl(documentId)).downloadUrl,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // An empty id is a sheet that has not settled yet, not a document to fetch.
    enabled: (options.enabled ?? true) && documentId !== '',
  })
}
